import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams  } from "react-router-dom";
import { Row, Col } from 'react-bootstrap';
import { useTranslation } from "react-i18next";

import LeftSection from "../../components/PreLogin/LoginPage/LeftSection.js";
import RightFooter from "../../components/PreLogin/RightFooter";

import Button from "../../components/ui/Button";

import { sideModalBackArrowIcon } from "../../utils/icons.js";

import "./index.scss";

import { useSelector, useDispatch } from 'react-redux';
import { verify2FaAccount, getLoggedInUserDetails } from '../../store/actions/loginAction.js';

function safeNext(raw) {
    if (!raw) return '/';
    try {
        const decoded = decodeURIComponent(raw);
        // Must start with a single "/" and not "//" or "/\"
        if (!decoded.startsWith('/') || decoded.startsWith('//') || decoded.startsWith('/\\')) {
            return '/';
        }
        return decoded;
    } catch {
        return '/';
    }
}

const VerifyAccount = () => {

    const response = useSelector((state) => state.login?.verify2FaResponse);
    const userDetailsResponse = useSelector((state)=> state?.login?.loggedInUserDetailResponse)

    const dispatch = useDispatch();
   
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [otp, setOtp] = useState(new Array(6).fill(""));
    const otpBoxReference = useRef([]);
    const [isLoading, setIsLoading] = useState(false);
    const [verifyAccountError, setVerifyAccountError] = useState('');

    const next = safeNext(searchParams.get('next'));

    const handleChange = (e, index) => {
        const value = e.target.value.replace(/\D/g, ''); // Accept only digits
        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);

        if (value && index < otp.length - 1) {
            otpBoxReference.current[index + 1]?.focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedText = e.clipboardData.getData('text').trim();
        const digits = pastedText.replace(/\D/g, '').slice(0, 6).split('');

        if (digits.length === 0) return;

        const newOtp = [...otp];
        digits.forEach((digit, idx) => {
            if (idx < newOtp.length) {
                newOtp[idx] = digit;
                if (otpBoxReference.current[idx]) {
                    otpBoxReference.current[idx].value = digit;
                }
            }
        });

        setOtp(newOtp);

        // Move focus to last filled input
        const lastIdx = Math.min(digits.length - 1, otpBoxReference.current.length - 1);
        otpBoxReference.current[lastIdx]?.focus();
    };

    const handleBackspaceAndEnter = (e, index) => {
        if (e.key === "Backspace" && !e.target.value && index > 0) {
            // Check if there’s a previous input box before focusing
            if (otpBoxReference.current[index - 1]) {
                otpBoxReference.current[index - 1].focus();
            }
        }
        if (e.key === "Enter" && e.target.value && index < otp.length - 1) {
            if (otpBoxReference.current[index + 1]) {
                otpBoxReference.current[index + 1].focus();
            }
        }
    };

    const isDisabledButton = () => {
        return otp.includes("");
    };

    const handleKeyDown = (event) => {
        if (event.key === "Enter" && !isDisabledButton()) {
            buttonClicked();
        }
    }

    const buttonClicked = () => {
        let obj = {
            totp_code: otp.join("")
        };
        setIsLoading(true)
        dispatch(verify2FaAccount(obj));
    };

    const goBack = () => {
        navigate('/auth/login');
    };

    useEffect(()=>{
        if(response?.detail?.status === "success") {
            dispatch(getLoggedInUserDetails());
        }

        if(response?.status === "failed") {
            setVerifyAccountError(response?.description)
            setIsLoading(false)
        } 
    },[response, navigate, dispatch])

    useEffect(()=>{
        if(userDetailsResponse !== null) {
            setIsLoading(false);
            let obj = {
                ...userDetailsResponse,
                language: "en"
            }
            localStorage.setItem('loggedInUserDetails', JSON.stringify(obj))
            if(!next) {
                navigate("/scan-info/exposure/dashboard?customer=all&organization=all&domain=all&scanId=null")                
            } else {
                navigate(next, { replace: true });
            }
        }
    },[userDetailsResponse, navigate, next])

    return (
        <div className="verifyAccount-container">
            <Row className='h-100 m-0'>
                <Col md="6">
                    <LeftSection />
                </Col>
                <Col md="6">
                    <div className='verify-account-right-section px-4 mx-3 h-100' onKeyDown={handleKeyDown}>
                        <div className='right-container'>
                            <span onClick={goBack} className="go-back-icon">{sideModalBackArrowIcon()}</span>
                            <h1 className='mb-4 mt-5'>{t("loginPage.verifyAccount.heading")}</h1>
                            <p className="description">{t("loginPage.verifyAccount.secondaryText")}</p>
                            {verifyAccountError !== "" && <p className="error-msg text-center mt-3">{verifyAccountError}</p>}
                            <div className='input-container mt-4'>
                                <p className='otp-label mb-3'>2FA Code</p>
                                <div className="d-flex" onPaste={handlePaste}>
                                    {otp.map((digit, index) => (
                                        <input 
                                            key={index}
                                            type="text"
                                            inputMode="numeric"
                                            pattern="\d*"
                                            value={digit}
                                            maxLength={1}
                                            onChange={(e) => handleChange(e, index)}
                                            onKeyUp={(e) => handleBackspaceAndEnter(e, index)}
                                            ref={(reference) => (otpBoxReference.current[index] = reference)}
                                            className="otp-input-box"
                                        />
                                    ))}
                                </div>
                                <div className='submit-button mt-4'>
                                    {!isLoading && <Button 
                                        buttonText={"Continue"} 
                                        buttonType="primary" 
                                        buttonClicked={buttonClicked} 
                                        disableButton={isDisabledButton()}
                                    />}
                                    {isLoading && <Button 
                                        buttonText={"Loading..."} 
                                        buttonType="primary" 
                                        buttonClicked={buttonClicked} 
                                        disableButton={true}
                                    />}
                                </div>
                            </div>
                        </div>
                        <RightFooter />
                    </div>
                </Col>
            </Row>
        </div>
    );
};

export default VerifyAccount;